import { Button, RadioButton } from 'lego-on-react';
import React, { useCallback, useState } from 'react';
import { useAsync } from 'react-use';

import i18n from '@yandex-int/i18n';
import { Channel } from 'client/model/skill';
import * as keyset from 'i18n/IntentsPage';
import block from 'propmods';

import { getVerstehenLogs } from '../../api/services/grammar';
import { CrossIcon } from '../../icons/cross';
import { GrammarIntentEditableFields } from '../../model/grammar';
import { Hint } from '../Hint/Hint';
import { Loading } from '../Loading/Loading';

import ArrowIcon from '../../icons/arrow_back.svg';
import LikeIcon from '../../icons/like.svg';

import './GrammarPageVerstehen.scss';

const t = i18n(keyset);
const b = block('GrammarPageVerstehen');

interface TestComponentProps {
    testValue: string;
    id: number;
    approvedTestsHandler: (id: number) => void;
    declinedTestsHandler: (id: number) => void;
    occurrence: number;
    indexStatus: VerstehenIndexes;
}
interface VerstehenAnswer {
    index_payload: {
        parsed: string;
        percentage: number;
    };
    idx: number;
    payload: {
        occurrence_rate: number;
        count: number;
    };
    score: number;
    text: string;
}
interface LogsState {
    text: string;
    id: number;
    occurrence: number;
}

const filterLogs = (logsAll: LogsState[], filterSet: Set<number>, currTests: string) => {
    const testsWithoutEmpty = currTests.split('\n').filter((test) => test !== '');
    const filtered: string[] = logsAll.filter((x) => filterSet.has(x.id)).map((x) => x.text);
    const uniqueTests = new Set([...testsWithoutEmpty, ...filtered]);
    return [...uniqueTests].join('\n');
};

const textWithSlots = (text: string) => {
    const regular = /\(\w+\)/g;
    text = _.escape(text);
    return text.replace(regular, (r) => `<span style="color: #00f">${r}</span>`);
};

const TestComponent: React.FC<TestComponentProps> = ({
    testValue,
    id,
    approvedTestsHandler,
    declinedTestsHandler,
    occurrence,
    indexStatus,
}) => {
    const [buttonsState, setButtonsState] = useState({ approved: false, declined: false });

    const onApproveToggle = useCallback(() => {
        approvedTestsHandler(id);
        setButtonsState({ approved: !buttonsState.approved, declined: false });
    }, [approvedTestsHandler, id, buttonsState.approved]);
    const onDeclineToggle = useCallback(() => {
        declinedTestsHandler(id);
        setButtonsState({ approved: false, declined: !buttonsState.declined });
    }, [declinedTestsHandler, id, buttonsState.declined]);
    return (
        <div {...b('test-element')}>
            <div {...b('test-content')} dangerouslySetInnerHTML={{ __html: textWithSlots(testValue) }} />
            <div {...b('test-info')}>
                {occurrence}
                {indexStatus === 'Granet' && '%'}
            </div>
            <div {...b('test-radio')}>
                <Button
                    theme={'pseudo'}
                    pin={'round-round'}
                    onClick={onApproveToggle}
                    checked={buttonsState.approved}
                    cls={buttonsState.approved ? 'GrammarPageVerstehen__approved' : ''}
                >
                    <LikeIcon />
                </Button>
                <Button
                    theme={'pseudo'}
                    pin={'round-round'}
                    onClick={onDeclineToggle}
                    checked={buttonsState.declined}
                    cls={buttonsState.declined ? 'GrammarPageVerstehen__declined' : ''}
                >
                    <LikeIcon />
                </Button>
            </div>
        </div>
    );
};

interface Props {
    channel: Channel;
    onShow: (pos: string, neg: string) => void;
    skillId: string;
    intentSettings: GrammarIntentEditableFields;
    entities: string;
    setIsChanged: (isChanged: boolean) => void;
    onClose: () => void;
}

export type VerstehenIndexes = 'Granet' | 'DSSM';

export const GrammarPageVerstehen: React.FC<Props> = ({
    channel,
    onShow,
    skillId,
    intentSettings,
    entities,
    setIsChanged,
    onClose,
}) => {
    const [indexStatus, setIndexStatus] = useState<VerstehenIndexes>('Granet');
    const [savedLogs, setSavedLogs] = useState<string[]>([]);
    const [approvedTests, setApprovedTests] = useState<Set<number>>(new Set());
    const [declinedTests, setDeclinedTests] = useState<Set<number>>(new Set());
    const approvedTestsHandler = useCallback(
        (id: number) => {
            setIsChanged(true);
            if (approvedTests.has(id)) {
                approvedTests.delete(id);
                setApprovedTests(new Set([...approvedTests]));
            } else {
                approvedTests.add(id);
                setApprovedTests(new Set([...approvedTests]));
            }
        },
        [approvedTests, setIsChanged],
    );
    const onShowHandler = useCallback(() => {
        onShow(intentSettings.positiveTests, intentSettings.negativeTests);
    }, [intentSettings, onShow]);
    const declinedTestsHandler = useCallback(
        (id: number) => {
            setIsChanged(true);
            if (declinedTests.has(id)) {
                declinedTests.delete(id);
                setDeclinedTests(new Set([...declinedTests]));
            } else {
                declinedTests.add(id);
                setDeclinedTests(new Set([...declinedTests]));
            }
        },
        [declinedTests, setIsChanged],
    );
    const logs = useAsync(async () => {
        let settings: GrammarIntentEditableFields = { ...intentSettings };
        if (indexStatus === 'DSSM') {
            settings = { ...intentSettings, sourceText: savedLogs.join('\n') };
        }
        const logsArray = await getVerstehenLogs({ skillId, intentSettings: settings, entities }, indexStatus, channel)
            .then((log) => log.search_result)
            .catch((e) => (e.code === 400 ? null : e));
        if (logsArray === null) {
            return undefined;
        }
        // eslint-disable-next-line camelcase
        const matchedLogs: LogsState[] = logsArray.map((x: VerstehenAnswer) => {
            let occurrence_rate;
            if (indexStatus === 'Granet') {
                occurrence_rate = ((x.index_payload?.percentage || x.score) * 100).toFixed(1);
                return {
                    text: x.index_payload.parsed,
                    id: x.idx,
                    occurrence: Number(occurrence_rate) < 0.1 ? '< 0.1' : occurrence_rate,
                };
            }
            return {
                text: x.text,
                id: x.idx,
                occurrence: x.score.toFixed(2),
            };
        });
        if (indexStatus === 'Granet') {
            setSavedLogs(matchedLogs.map((x) => x.text));
        }
        return matchedLogs;
    }, [indexStatus]);
    const onExport = useCallback(() => {
        if (logs.value === undefined) {
            return;
        }
        onShow(
            filterLogs(logs.value, approvedTests, intentSettings.positiveTests),
            filterLogs(logs.value, declinedTests, intentSettings.negativeTests),
        );
    }, [approvedTests, declinedTests, intentSettings.negativeTests, intentSettings.positiveTests, logs.value, onShow]);

    return (
        <div {...b()}>
            <div {...b('header')}>
                <div {...b('header-h')}>
                    <div {...b('back')} onClick={onShowHandler}>
                        <ArrowIcon color="#979797" />
                    </div>
                    <h2>{t('header-verstehen')}</h2>
                </div>
                <RadioButton
                    theme="normal"
                    size="s"
                    onChange={(e) => setIndexStatus(e.target.value as VerstehenIndexes)}
                >
                    <RadioButton.Radio key={'Granet'} value={'Granet'} checked={indexStatus === 'Granet'}>
                        {t('title-grammar')}
                    </RadioButton.Radio>
                    <RadioButton.Radio key={'DSSM'} value={'DSSM'} checked={indexStatus === 'DSSM'}>
                        {t('search-engine')}
                    </RadioButton.Radio>
                </RadioButton>
                <div {...b('close')} onClick={onClose}>
                    <CrossIcon color="#979797" />
                </div>
            </div>
            <div {...b('field')}>
                {logs.loading && <Loading centered grow />}
                {logs.error && <div {...b('error')}>{t('error-server-side')}</div>}
                {!logs.loading && !logs.error && logs.value === undefined && (
                    <div {...b('error')}>{t('error-grammar')}</div>
                )}
                {logs.value !== undefined && logs.value.length === 0 ? (
                    <div {...b('error')}>{t('error-no-logs')}</div>
                ) : (
                    <>
                        <div {...b('field__headers-menu')}>
                            <div {...b('field__header-logs')}>{t('label-requests')}</div>
                            <div {...b('field__header-occ')}>
                                {indexStatus === 'DSSM' ? t('label-proximity') : t('label-frequency')}
                            </div>
                            <div {...b('field__header-buttons')}>{t('button-add-tests')}</div>
                        </div>
                        {logs.value !== undefined &&
                            logs.value.map((log) => {
                                return (
                                    <TestComponent
                                        testValue={log.text}
                                        id={log.id}
                                        approvedTestsHandler={approvedTestsHandler}
                                        declinedTestsHandler={declinedTestsHandler}
                                        key={`log-${log.id}`}
                                        occurrence={log.occurrence}
                                        indexStatus={indexStatus}
                                    />
                                );
                            })}
                    </>
                )}
            </div>
            <div {...b('export-button')}>
                <Button
                    theme={'action'}
                    size={'s'}
                    pin={'round-round'}
                    onClick={onExport}
                    disabled={!approvedTests.size && !declinedTests.size}
                >
                    {t('button-add-tests')}
                </Button>
                <span {...b('hint')}>
                    <Hint hint={t('hint-add-tests')} />
                </span>
            </div>
        </div>
    );
};
